import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Check, X, ShieldOff } from "lucide-react";

/**
 * Master-data editor used by both /administration/departments and /administration/designations.
 *
 * Props:
 *  - resource: "departments" | "designations"
 *  - title: e.g. "Departments"
 *  - eyebrow: e.g. "Administration · Directory"
 *  - description: helper copy for the header
 *  - seedable: whether the page shows the "Seed defaults" button (only meaningful when list is empty)
 */
export default function DirectoryEditor({ resource, title, eyebrow, description, seedable = true }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canWrite = ["managing_director", "hr_manager", "hr_admin", "super_admin"].includes(user?.role);
  const testid = `dir-${resource}`;

  const [newName, setNewName] = useState("");
  const [editing, setEditing] = useState(null); // { id, name }
  const [pendingDelete, setPendingDelete] = useState(null);

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["directory", resource],
    queryFn: async () => (await api.get(`/directory/${resource}`)).data,
  });

  const createMut = useMutation({
    mutationFn: (name) => api.post(`/directory/${resource}`, { name }),
    onSuccess: () => { toast.success(`${title.slice(0, -1)} added`); setNewName(""); qc.invalidateQueries({ queryKey: ["directory", resource] }); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, name }) => api.patch(`/directory/${resource}/${id}`, { name }),
    onSuccess: () => { toast.success(`${title.slice(0, -1)} updated`); setEditing(null); qc.invalidateQueries({ queryKey: ["directory", resource] }); qc.invalidateQueries({ queryKey: ["employees"] }); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const deleteMut = useMutation({
    mutationFn: (id) => api.delete(`/directory/${resource}/${id}`),
    onSuccess: () => { toast.success(`${title.slice(0, -1)} deleted`); setPendingDelete(null); qc.invalidateQueries({ queryKey: ["directory", resource] }); },
    onError: (e) => { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setPendingDelete(null); },
  });
  const seedMut = useMutation({
    mutationFn: () => api.post("/directory/seed-defaults"),
    onSuccess: (res) => {
      const c = res.data.created || {};
      toast.success(`Seeded ${c.departments || 0} departments · ${c.designations || 0} designations`);
      qc.invalidateQueries({ queryKey: ["directory"] });
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  if (!canWrite) {
    return (
      <div className="p-8 max-w-2xl" data-testid={`${testid}-page`}>
        <div className="border border-amber-200 bg-amber-50 text-amber-900 rounded-md p-4 text-sm flex items-start gap-2">
          <ShieldOff className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>Only the Managing Director or HR Manager can manage {title}.</div>
        </div>
      </div>
    );
  }

  const submitCreate = (e) => { e.preventDefault(); if (!newName.trim()) return; createMut.mutate(newName.trim()); };
  const commitEdit = () => { if (!editing?.name?.trim()) return; updateMut.mutate({ id: editing.id, name: editing.name.trim() }); };

  return (
    <div data-testid={`${testid}-page`}>
      <PageHeader eyebrow={eyebrow} title={title} description={description} />
      <div className="max-w-3xl mx-auto px-8 py-6 space-y-6">
        <form onSubmit={submitCreate} className="flex items-center gap-2">
          <Input
            placeholder={`Add a new ${title.slice(0, -1).toLowerCase()} (e.g. "BIM")`}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            data-testid={`${testid}-new-input`}
            className="h-10"
          />
          <Button type="submit" disabled={createMut.isPending || !newName.trim()} data-testid={`${testid}-new-btn`}>
            <Plus className="w-4 h-4 mr-1.5" /> Add
          </Button>
          {seedable && rows.length === 0 && !isLoading && (
            <Button type="button" variant="outline" onClick={() => seedMut.mutate()} disabled={seedMut.isPending} data-testid={`${testid}-seed-btn`}>
              Seed starter list
            </Button>
          )}
        </form>

        <div className="border border-zinc-200 rounded-lg bg-white overflow-hidden">
          <table className="w-full text-sm" data-testid={`${testid}-table`}>
            <thead className="bg-zinc-50 border-b border-zinc-200 text-[10px] uppercase tracking-widest text-zinc-500">
              <tr>
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-4 py-3">Created</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {rows.map((r) => (
                <tr key={r.id} data-testid={`${testid}-row-${r.id}`}>
                  <td className="px-4 py-3">
                    {editing?.id === r.id ? (
                      <Input
                        value={editing.name}
                        onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                        onKeyDown={(e) => { if (e.key === "Enter") commitEdit(); if (e.key === "Escape") setEditing(null); }}
                        autoFocus
                        className="h-8"
                        data-testid={`${testid}-edit-input-${r.id}`}
                      />
                    ) : (
                      <span className="font-medium">{r.name}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-zinc-500 font-mono">
                    {r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {editing?.id === r.id ? (
                      <div className="inline-flex items-center gap-1">
                        <Button size="sm" onClick={commitEdit} disabled={updateMut.isPending} data-testid={`${testid}-save-${r.id}`}>
                          <Check className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setEditing(null)} data-testid={`${testid}-cancel-${r.id}`}>
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    ) : (
                      <div className="inline-flex items-center gap-1">
                        <button
                          className="p-1.5 rounded-sm hover:bg-zinc-100 text-zinc-600"
                          onClick={() => setEditing({ id: r.id, name: r.name })}
                          data-testid={`${testid}-edit-${r.id}`}
                          aria-label="Edit"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          className="p-1.5 rounded-sm hover:bg-red-50 text-red-600"
                          onClick={() => setPendingDelete(r)}
                          data-testid={`${testid}-delete-${r.id}`}
                          aria-label="Delete"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && !isLoading && (
                <tr>
                  <td colSpan={3} className="py-12 text-center text-sm text-zinc-500">
                    No {title.toLowerCase()} yet. Add one above{seedable ? " or seed a starter list" : ""}.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="text-[11px] text-zinc-500 flex items-center gap-2">
          <Badge variant="outline" className="text-[10px]">{rows.length}</Badge>
          <span>Total {title.toLowerCase()}. Renaming propagates to every employee automatically. Deletion is blocked while any active employee is still assigned.</span>
        </div>
      </div>

      <AlertDialog open={!!pendingDelete} onOpenChange={(v) => !v && setPendingDelete(null)}>
        <AlertDialogContent data-testid={`${testid}-confirm-dialog`}>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {pendingDelete?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes it from every future dropdown. If any active employee currently uses this value the delete will be blocked.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`${testid}-confirm-cancel`}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => pendingDelete && deleteMut.mutate(pendingDelete.id)}
              data-testid={`${testid}-confirm-delete`}
            >
              {deleteMut.isPending ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
